import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Logo } from "@/components/Logo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Loader2, Mail, ShieldCheck } from "lucide-react";
import { GoogleAuthButton } from "@/components/GoogleAuthButton";

export default function Login() {
    const { login, loginVerify } = useAuth();
    const navigate = useNavigate();
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState("");

    // MFA step state
    const [mfa, setMfa] = useState(null); // { challengeToken, emailHint, expiresInMinutes } | null
    const [code, setCode] = useState("");
    const [rememberDevice, setRememberDevice] = useState(true);

    // Surface any error query-param left by the Google OAuth callback (e.g. ?oauth_error=cancelled).
    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        const oauthErr = params.get("oauth_error");
        if (!oauthErr) return;
        const msg = {
            cancelled: "Google sign-in was cancelled.",
            unverified_email: "Your Google account email isn't verified. Please verify it with Google first.",
            invalid_state: "Google sign-in link expired. Please try again.",
            missing_params: "Google sign-in didn't complete. Please try again.",
        }[oauthErr] || "Google sign-in failed. Please try again or use email + password.";
        setErr(msg);
        // Clean the URL so a refresh doesn't resurrect the error.
        params.delete("oauth_error");
        const clean = window.location.pathname + (params.toString() ? `?${params.toString()}` : "");
        window.history.replaceState({}, "", clean);
    }, []);

    const onSubmit = async (e) => {
        e.preventDefault();
        setErr(""); setBusy(true);
        const res = await login(email, password);
        setBusy(false);
        if (!res.ok) {
            setErr(res.error || "Login failed");
            return;
        }
        if (res.mfaRequired) {
            setMfa({
                challengeToken: res.challengeToken,
                emailHint: res.emailHint,
                expiresInMinutes: res.expiresInMinutes,
            });
            toast.success(`We emailed a 6-digit code to ${res.emailHint}`);
            return;
        }
        toast.success("Welcome back");
        navigate("/app", { replace: true });
    };

    const onVerify = async (e) => {
        e.preventDefault();
        setErr(""); setBusy(true);
        const res = await loginVerify({
            challengeToken: mfa.challengeToken,
            code: code.trim(),
            rememberDevice,
        });
        setBusy(false);
        if (!res.ok) {
            setErr(res.error || "Verification failed");
            return;
        }
        toast.success("Welcome back");
        navigate("/app", { replace: true });
    };

    const resetFlow = () => {
        setMfa(null);
        setCode("");
        setErr("");
    };

    return (
        <div className="min-h-screen grid lg:grid-cols-2 bg-sand-50">
            <div className="hidden lg:flex relative bg-deep-ocean text-white p-14 flex-col justify-between">
                <Link to="/"><Logo /></Link>
                <div>
                    <h2 className="font-heading text-4xl tracking-tight max-w-md">"Open a clean set of books in Grand Cayman before lunch."</h2>
                    <p className="mt-4 text-white/60 text-sm">— A founder we made smile</p>
                </div>
                <div className="text-xs text-white/50">© {new Date().getFullYear()} BlueLedger</div>
                <div className="absolute right-0 bottom-0 w-1/2 h-1/2 bg-secondary/10 blur-3xl rounded-full pointer-events-none" />
            </div>
            <div className="flex flex-col justify-center px-6 sm:px-12 py-12 max-w-xl w-full mx-auto">
                <div className="lg:hidden mb-10"><Link to="/"><Logo /></Link></div>

                {!mfa ? (
                    <>
                        <h1 className="font-heading text-4xl text-ocean-700">Welcome back</h1>
                        <p className="mt-2 text-muted-foreground">Sign in to continue managing your books.</p>

                        <div className="mt-8">
                            <GoogleAuthButton testId="login-google-btn" />
                        </div>
                        <div className="relative my-6" aria-hidden="true">
                            <div className="absolute inset-0 flex items-center">
                                <span className="w-full border-t border-sand-300" />
                            </div>
                            <div className="relative flex justify-center text-xs uppercase tracking-wider">
                                <span className="bg-sand-50 px-3 text-muted-foreground">or sign in with email</span>
                            </div>
                        </div>

                        <form onSubmit={onSubmit} className="space-y-5">
                            <div>
                                <Label htmlFor="email">Email</Label>
                                <Input id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required data-testid="login-email" className="mt-1.5 h-11" />
                            </div>
                            <div>
                                <div className="flex items-center justify-between">
                                    <Label htmlFor="password">Password</Label>
                                    <Link to="/forgot" className="text-xs text-muted-foreground hover:text-secondary">Forgot?</Link>
                                </div>
                                <Input id="password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required data-testid="login-password" className="mt-1.5 h-11" />
                            </div>
                            {err && <div className="text-sm text-destructive" data-testid="login-error">{err}</div>}
                            <Button type="submit" className="w-full h-11 bg-ocean-700 hover:bg-ocean-700/90 text-white rounded-xl" disabled={busy} data-testid="login-submit">
                                {busy && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}Sign in
                            </Button>
                        </form>

                        <p className="mt-8 text-sm text-muted-foreground">
                            New to BlueLedger? <Link to="/register" className="text-secondary font-medium hover:underline">Create an account</Link>
                        </p>
                    </>
                ) : (
                    <div data-testid="login-mfa-step">
                        <div className="inline-flex items-center gap-2 text-secondary-dark bg-secondary/10 rounded-full px-3 py-1 text-xs font-medium">
                            <ShieldCheck className="h-3.5 w-3.5" /> Two-step verification
                        </div>
                        <h1 className="font-heading text-4xl text-ocean-700 mt-4">Check your email</h1>
                        <p className="mt-2 text-muted-foreground">
                            We sent a 6-digit code to <span className="text-ocean-700 font-medium">{mfa.emailHint}</span>. Enter it below to finish signing in. It expires in {mfa.expiresInMinutes} minutes.
                        </p>

                        <form onSubmit={onVerify} className="mt-8 space-y-5">
                            <div>
                                <Label htmlFor="code">Sign-in code</Label>
                                <Input
                                    id="code"
                                    type="text"
                                    inputMode="numeric"
                                    autoComplete="one-time-code"
                                    maxLength={10}
                                    value={code}
                                    onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
                                    required
                                    autoFocus
                                    data-testid="login-mfa-code"
                                    className="mt-1.5 h-12 text-center tracking-[0.5em] font-mono text-xl"
                                    placeholder="123456"
                                />
                            </div>

                            <label className="flex items-start gap-3 cursor-pointer select-none">
                                <input
                                    type="checkbox"
                                    checked={rememberDevice}
                                    onChange={(e) => setRememberDevice(e.target.checked)}
                                    className="mt-1 h-4 w-4 accent-secondary"
                                    data-testid="login-mfa-remember"
                                />
                                <div className="text-sm">
                                    <div className="font-medium text-ocean-700">Remember this device for 30 days</div>
                                    <div className="text-xs text-muted-foreground mt-0.5">
                                        Skip the code on this browser for 30 days. Don't enable on shared or public computers.
                                    </div>
                                </div>
                            </label>

                            {err && <div className="text-sm text-destructive" data-testid="login-mfa-error">{err}</div>}

                            <Button type="submit" disabled={busy || code.length < 4} className="w-full h-11 bg-ocean-700 hover:bg-ocean-700/90 text-white rounded-xl" data-testid="login-mfa-submit">
                                {busy && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}Verify & sign in
                            </Button>
                        </form>

                        <button
                            type="button"
                            onClick={resetFlow}
                            className="mt-6 text-xs text-muted-foreground hover:text-secondary inline-flex items-center gap-1"
                            data-testid="login-mfa-back"
                        >
                            <Mail className="h-3 w-3" /> Use a different email
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
}
